'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import YoutubePlayIcon from '@repo/ui/src/components/Icons/YoutubePlayIcon';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { YoutubeLinkProps } from '@repo/ui/src/types';
import Image from '@repo/ui/src/components/Image';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { classNames } from '@repo/ui/src/utils/classNames';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';

interface Props {
  youtubeLink: YoutubeLinkProps;
}

const RecipeVideo = ({ youtubeLink }: Props) => {
  const { push } = useModal(ModalID.video);
  const { t } = useTranslation();

  if (!youtubeLink) return null;

  const youtubeLinkThumbnail = youtubeLink?.uploadedThumbnail
    ? buildImageSrc(youtubeLink.uploadedThumbnail[0], 200, 3, 2)
    : youtubeLink?.thumbnailUrl;

  const hasMedia = youtubeLink?.thumbnailVideo || youtubeLinkThumbnail;

  return (
    <div
      className={classNames(
        'tw-w-full tw-items-center tw-border tw-rounded tw-border-warm-400 tw-mb-8',
        hasMedia && 'tw-min-h-[316px] sm:tw-min-h-[284px] tw-grid tw-grid-rows-[1.8fr_1fr] sm:tw-grid-rows-1 sm:tw-grid-cols-[1.3fr_1fr] tw-gap-1 sm:tw-gap-4'
      )}
    >
      {hasMedia && (
        <div className="tw-h-full tw-relative tw-w-full tw-overflow-hidden">
          {youtubeLink.thumbnailVideo ? (
            <BackgroundVideo
              video={youtubeLink.thumbnailVideo}
              playButton={true}
              width={500}
              className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-object-top"
            />
          ) : (
            <Image
              src={youtubeLinkThumbnail!}
              alt={`${youtubeLink.name} thumbnail`}
              layout="fill"
              objectFit="cover"
            />
          )}
        </div>
      )}
      <div className={classNames(hasMedia ? 'tw-p-4' : 'tw-p-12')}>
        {youtubeLink.name && <p className="tra-text-web-heading-5">{youtubeLink.name}</p>}
        <Button
          variant={ButtonVariant.secondary}
          size="small"
          onClick={() => push({ youtubeLink })}
          aria-label="play button"
          className="tw-mt-4"
        >
          <YoutubePlayIcon height="20px" className="tw-h-full tw-inline-flex tw-align-sub" />
          <span className="tw-ml-2">
            {t('recipeContent.watchVideoLabel', undefined, { default: 'Watch Video' })}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default RecipeVideo;
